import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useErrorDemo } from '../hooks/useErrorDemo';
import { errorFormSchema, type ErrorFormValues } from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

export function ErrorPanel() {
  const { t } = useTranslation();
  const { mutate, isPending, reset } = useErrorDemo();
  const [demoResult, setDemoResult] = useState<ApiError | null>(null);

  const ERROR_CODES: { value: string; label: string; http: number; desc: string }[] = [
    { value: 'COMMON-404-001', label: 'COMMON-404-001', http: 404, desc: t('showcase.error.codes.notFound') },
    { value: 'COMMON-409-001', label: 'COMMON-409-001', http: 409, desc: t('showcase.error.codes.emailDup') },
    { value: 'COMMON-500-001', label: 'COMMON-500-001', http: 500, desc: t('showcase.error.codes.unknown') },
    { value: 'AUTH-401-001', label: 'AUTH-401-001', http: 401, desc: t('showcase.error.codes.tokenExpired') },
    { value: 'AUTH-403-001', label: 'AUTH-403-001', http: 403, desc: t('showcase.error.codes.forbidden') },
  ];

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ErrorFormValues>({
    resolver: zodResolver(errorFormSchema),
    defaultValues: { code: 'COMMON-404-001', argsStr: '42' },
  });

  const selectedCode = watch('code');
  const selectedCodeInfo = ERROR_CODES.find((e) => e.value === selectedCode);

  function onSubmit(values: ErrorFormValues) {
    setDemoResult(null);
    const args = values.argsStr
      ? values.argsStr.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    mutate(
      { code: values.code, args },
      {
        onError: (err: ApiError) => {
          setDemoResult(err);
        },
        onSuccess: () => {
          // Should not happen; server always throws
        },
      }
    );
  }

  function getHttpColor(statusCode?: number): string {
    if (!statusCode) return 'var(--color-text-muted)';
    if (statusCode < 400) return 'var(--color-success)';
    if (statusCode < 500) return 'var(--color-warning)';
    return 'var(--color-danger)';
  }

  return (
    <section aria-labelledby="error-panel-title">
      <h2
        id="error-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.error.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.error.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* ErrorCode selector */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="error-code"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.error.code')}
          </label>
          <select
            id="error-code"
            aria-label={t('showcase.error.code')}
            aria-invalid={!!errors.code}
            {...register('code')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.code ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
            }}
          >
            {ERROR_CODES.map((ec) => (
              <option key={ec.value} value={ec.value}>
                {ec.label} ({ec.http}) — {ec.desc}
              </option>
            ))}
          </select>
          {selectedCodeInfo && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
              HTTP {selectedCodeInfo.http} — {selectedCodeInfo.desc}
            </p>
          )}
        </div>

        {/* Args input */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="error-args"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.error.args')}
          </label>
          <input
            id="error-args"
            type="text"
            placeholder="42"
            aria-label={t('showcase.error.args')}
            aria-invalid={!!errors.argsStr}
            {...register('argsStr')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.argsStr ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending ? t('showcase.error.loading') : t('showcase.error.throw')}
          </button>
          {demoResult && (
            <button
              type="button"
              onClick={() => {
                setDemoResult(null);
                reset();
              }}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Demo result card — intentional error response */}
      {demoResult && (
        <div
          role="region"
          aria-label={t('showcase.error.resultLabel')}
          style={{
            border: `1px solid ${getHttpColor(demoResult.statusCode)}`,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {/* R11-#288: status badge uses BE error.code + HTTP status directly */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)' }}>
            <code
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                color: getHttpColor(demoResult.statusCode),
                background: 'var(--color-surface-elevated)',
                border: `1px solid ${getHttpColor(demoResult.statusCode)}`,
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              HTTP {demoResult.statusCode ?? '?'}
            </code>
            <code
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {/* demoResult.code = BE ErrorResponse.error.code (e.g. COMMON-404-001) */}
              {demoResult.code}
            </code>
          </div>

          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>message</dt>
            <dd style={{ margin: 0, color: 'var(--color-text-primary)' }}>{demoResult.message}</dd>
            {demoResult.detail && (
              <>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>detail</dt>
                <dd style={{ margin: 0 }}>
                  <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                    {demoResult.detail}
                  </code>
                </dd>
              </>
            )}
            {demoResult.traceId && (
              <>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>traceId</dt>
                <dd style={{ margin: 0 }}>
                  <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {demoResult.traceId}
                  </code>
                </dd>
              </>
            )}
          </dl>

          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--spacing-3)',
              marginBottom: 0,
              fontStyle: 'italic',
            }}
          >
            {t('showcase.error.intendedResult')}
          </p>
        </div>
      )}

      {/* Learning message */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.error.result.learning')}
      </div>
    </section>
  );
}
